import { useEffect, useState } from 'react';
import { Handle, Position, useUpdateNodeInternals, useReactFlow } from 'reactflow';
import {
  CARD_BG,
  CARD_BORDER,
  CARD_BORDER_SELECTED,
  CARD_RADIUS,
  CARD_TEXT,
  SECTION_BG,
  SECTION_BORDER,
  inputStyle,
  selStyle,
} from '../constants';
import { SKILL_IO } from './EditableNode';
const CAMERA_OUTPUTS = [
  'camera_topics',
  'init_success',
  'init_failed',
  'streaming',
];

function InitializeCameraNode({ id, data, selected }) {
  const updateNodeInternals = useUpdateNodeInternals();
  const cameras = data.cameras || [];
  const nodeCollapsed = !!data.collapsed;

  // Output mappings state
  const [connections, setConnections] = useState(data.connections || [
    { targetId: null, mappings: [] }
  ]);

  useEffect(() => {
    setConnections(data.connections || [
      { targetId: null, mappings: [] }
    ]);
  }, [data.connections]);

  useEffect(() => {
    updateNodeInternals(id);
  }, [id, cameras, nodeCollapsed, updateNodeInternals]);

  // Sync output mappings to parent
  useEffect(() => {
    if (data.onChangeConnections) {
      data.onChangeConnections(id, connections);
    }
  }, [connections]);

  // Output mapping UI handlers
  const addMapping = () => {
    setConnections(prev =>
      prev.map(c => ({
        ...c,
        mappings: [...c.mappings, { from: '', to: '' }]
      }))
    );
  };

  const removeMapping = (idx) => {
    setConnections(prev =>
      prev.map(c => ({
        ...c,
        mappings: c.mappings.filter((_, i) => i !== idx)
      }))
    );
  };

  const updateMapping = (idx, field, value) => {
    setConnections(prev =>
      prev.map(c => ({
        ...c,
        mappings: c.mappings.map((m, i) =>
          i === idx ? { ...m, [field]: value } : m
        )
      }))
    );
  };

  const toggleNode = () =>
    data.onToggleInitCollapsed && data.onToggleInitCollapsed(id);

  const addCamera = () => data.onAddCamera && data.onAddCamera(id);
  const toggle = (idx) => data.onToggleCamera && data.onToggleCamera(id, idx);
  const remove = (idx) => data.onRemoveCamera && data.onRemoveCamera(id, idx);
  const change = (idx, field) => (e) => {
    const raw = e.target.value;
    const value = field === 'topic' ? raw : (raw === '' ? '' : Number(raw));
    data.onChangeCameraAt && data.onChangeCameraAt(id, idx, field, value);
  };

  const { getEdges, getNodes } = useReactFlow();
  const edges = getEdges();
  const nodes = getNodes();
  const outgoingEdges = edges.filter(e => e.source === id);

  const connectedInputs = outgoingEdges.map(e => {
    const targetNode = nodes.find(n => n.id === e.target);
    const skillType = targetNode?.data?.skillName || targetNode?.data?.label || '';
    // Use your SKILL_IO mapping (import or define at top of file)
    const inputs = SKILL_IO[skillType]?.inputs || [];
    return { targetId: e.target, inputs };
  });

  return (
    <div
      style={{
        padding: 10,
        border: selected ? CARD_BORDER_SELECTED : CARD_BORDER,
        borderRadius: CARD_RADIUS,
        background: CARD_BG,
        minWidth: 320,
        color: CARD_TEXT,
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: '#555' }} />

      {/* Header row (click to toggle collapse) */}
      <div
        onClick={toggleNode}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 24 }}>{nodeCollapsed ? '▸' : '▾'}</span>
          <div style={{ fontWeight: 700 }}>Initialize Camera</div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); addCamera(); }}
          title="Add camera"
          style={{
            background: '#2e7d32',
            color: '#fff',
            border: 'none',
            borderRadius: 999,
            width: 28,
            height: 28,
            cursor: 'pointer',
            fontSize: 18,
            lineHeight: '28px',
            textAlign: 'center',
          }}
        >
          +
        </button>
      </div>

      {/* Body (hidden when collapsed) */}
      {!nodeCollapsed && (
        <>
          {cameras.length === 0 ? (
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              No cameras. Click <span style={{ color: '#81c784' }}>+</span> to add one.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {cameras.map((cam, idx) => (
                <div key={idx} style={{ border: SECTION_BORDER, borderRadius: 8, overflow: 'hidden' }}>
                  <div
                    onClick={() => toggle(idx)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: '#2a3a4a',
                      padding: '6px 8px',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12 }}>{cam.collapsed ? '▸' : '▾'}</span>
                      <span style={{ fontSize: 13, opacity: 0.95 }}>
                        {`vision-module-${idx + 1}`}
                      </span>
                    </div>

                    {/* trashcan (delete) */}
                    <button
                      title="Delete camera"
                      onClick={(e) => { e.stopPropagation(); remove(idx); }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 4,
                        lineHeight: 0,
                        color: '#ff8a80',
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M9 3h6a1 1 0 0 1 1 1v1h4v2h-1v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7H4V5h4V4a1 1 0 0 1 1-1zm2 3h2V5h-2v1zM7 7v12h10V7H7zm3 2h2v8h-2V9zm4 0h2v8h-2V9z" />
                      </svg>
                    </button>
                  </div>

                  {!cam.collapsed && (
                    <div style={{ padding: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, background: '#22303f' }}>
                      <label style={{ fontSize: 12, opacity: 0.9 }}>Port</label>
                      <input
                        type="number"
                        value={cam.port ?? ''}
                        min={0}
                        step={1}
                        onChange={change(idx, 'port')}
                        style={inputStyle}
                        placeholder="5000"
                      />

                      <label style={{ fontSize: 12, opacity: 0.9 }}>Topic</label>
                      <select value={cam.topic ?? 'image_raw_1'} onChange={change(idx, 'topic')} style={selStyle}>
                        {['image_raw_1', 'image_raw_2', 'image_raw_3', 'image_raw_4'].map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>

                      <label style={{ fontSize: 12, opacity: 0.9 }}>Width</label>
                      <input
                        type="number"
                        value={cam.width ?? ''}
                        min={1}
                        step={1}
                        onChange={change(idx, 'width')}
                        style={inputStyle}
                        placeholder="640"
                      />

                      <label style={{ fontSize: 12, opacity: 0.9 }}>Height</label>
                      <input
                        type="number"
                        value={cam.height ?? ''}
                        min={1}
                        step={1}
                        onChange={change(idx, 'height')}
                        style={inputStyle}
                        placeholder="480"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Connections list (like EditableNode) */}
      <div style={{ marginTop: 12, background: SECTION_BG, borderRadius: 8, padding: 10 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Connections</div>
        {connections.length === 0 ? (
          <div style={{ fontSize: 12, opacity: 0.7 }}>No connections</div>
        ) : (
          connections.map((conn, connIdx) => {
            // Find inputs for this connection's target
            const inputObj = connectedInputs.find(ci => ci.targetId === conn.targetId);
            const inputs = inputObj?.inputs || [];
            const targetLabel = nodes.find(n => n.id === conn.targetId)?.data?.label || conn.targetId;
            return (
              <div key={conn.targetId} style={{ border: SECTION_BORDER, borderRadius: 8, marginBottom: 10, overflow: 'hidden' }}>
                {/* Connection header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: '#2a3a4a',
                    padding: '6px 8px',
                    userSelect: 'none',
                  }}
                >
                  <span style={{ fontSize: 13, opacity: 0.95 }}>{targetLabel}</span>
                  <button
                    title="Add output mapping"
                    onClick={() => {
                      setConnections(prev =>
                        prev.map((c, i) =>
                          i === connIdx
                            ? { ...c, mappings: [...c.mappings, { from: '', to: '' }] }
                            : c
                        )
                      );
                    }}
                    style={{
                      background: '#2e7d32',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 999,
                      width: 24,
                      height: 24,
                      cursor: 'pointer',
                      fontSize: 18,
                      lineHeight: '24px',
                      textAlign: 'center',
                    }}
                  >
                    +
                  </button>
                </div>
                {/* Output mappings for this connection */}
                <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 4, background: '#22303f' }}>
                  {conn.mappings.length === 0 && <div style={{ fontSize: 12, opacity: 0.7 }}>No outputs yet.</div>}
                  {conn.mappings.map((map, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <select
                        value={map.from}
                        onChange={e => {
                          setConnections(prev =>
                            prev.map((c, i) =>
                              i === connIdx
                                ? {
                                    ...c,
                                    mappings: c.mappings.map((m, mi) =>
                                      mi === idx ? { ...m, from: e.target.value } : m
                                    ),
                                  }
                                : c
                            )
                          );
                        }}
                        style={{ ...selStyle, flex: 1 }}
                      >
                        <option value="">From output</option>
                        {CAMERA_OUTPUTS.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <select
                        value={map.to}
                        onChange={e => {
                          setConnections(prev =>
                            prev.map((c, i) =>
                              i === connIdx
                                ? {
                                    ...c,
                                    mappings: c.mappings.map((m, mi) =>
                                      mi === idx ? { ...m, to: e.target.value } : m
                                    ),
                                  }
                                : c
                            )
                          );
                        }}
                        style={{ ...selStyle, flex: 1 }}
                      >
                        <option value="">To input</option>
                        {inputs.map(i => <option key={i} value={i}>{i}</option>)}
                      </select>
                      <button
                        onClick={() => {
                          setConnections(prev =>
                            prev.map((c, i) =>
                              i === connIdx
                                ? {
                                    ...c,
                                    mappings: c.mappings.filter((_, mi) => mi !== idx),
                                  }
                                : c
                            )
                          );
                        }}
                        style={{
                          fontSize: 12,
                          padding: '2px 4px',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#ff8a80',
                        }}
                      >
                        🗑
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      <Handle type="source" position={Position.Bottom} style={{ background: '#555', marginTop: 8 }} />
    </div>
  );
}

export default InitializeCameraNode;
